import { type FormItem, type FormSection, type RowMap } from "./types";

/**
 * Coerce one collection cell's resolved string value to the row-child item's
 * typed answer shape. The server resolves every collection cell to a string
 * (with empty values omitted upstream), so a bound `number`/`boolean` child
 * must be widened back to its native answer type here for the renderer + the
 * evaluator to treat it like a normally-entered answer.
 *
 * Coercion table:
 * - `number`   → `parseFloat`
 * - `boolean`  → `"true"`/`"false"` → the matching bool (any other literal is
 *   passed through verbatim — a malformed cell is never silently coerced to
 *   `false`)
 * - `date`     → verbatim `YYYY-MM-DD`
 * - `datetime` → verbatim RFC3339
 * - everything else (`text`/`select_one`/`select_multiple`/`pass_fail`/…) →
 *   verbatim string
 *
 * Callers pass only non-empty strings — an empty cell is dropped by `seedRows`
 * before this runs, so the answer key stays absent rather than seeded blank.
 */
export function coerceSeedValue(item: FormItem, raw: string): unknown {
  switch (item.type) {
    case "number":
      return parseFloat(raw);
    case "boolean":
      if (raw === "true") return true;
      if (raw === "false") return false;
      return raw;
    default:
      return raw;
  }
}

/**
 * Build the seeded `RowMap[]` for one repeating section from its resolved
 * collection rows. Each output row maps a child item's `link_id` to the coerced
 * value of the collection cell keyed by that child's `read_source`. A child
 * with no `read_source` (a tester-entered finding column) is never seeded; a
 * collection cell that is missing or an empty string leaves the child's key
 * ABSENT (never a blank answer). Pure + dependency-free so both the web
 * composer and the mobile prefill patch share one implementation.
 */
export function seedRows(
  section: FormSection,
  collectionRows: Array<Record<string, string>>,
): RowMap[] {
  return collectionRows.map((collectionRow) => {
    const row: RowMap = {};
    for (const item of section.items) {
      const source = item.read_source;
      if (source === undefined) continue;
      const raw = collectionRow[source];
      if (raw === undefined || raw === "") continue;
      row[item.link_id] = coerceSeedValue(item, raw);
    }
    return row;
  });
}
